import React from 'react';
import { motion } from 'framer-motion';
import LogoImage from './Images/LogoImage.png';
import GymGuardianImage from './Images/Gym Guardian.png';
import PeptideCalculatorImage from './Images/Peptide Calculator.png';
import CSharpProjectsImage from './Images/csharp_projects.png';
import MilesPartyPackImage from './Images/Miles Party Pack.png';
import SouthwestAirlinesImage from './Images/Southwest Airlines Golden EAgles.jpeg';
import FaceIDProImage from './Images/faceid_pro_logo.png';
import GICCImage from './Images/gicc_project_card.svg';

import GitHubIcon from './Icons/GitHubIcon';
import ExternalLinkIcon from './Icons/ExternalLinkIcon';

export const projects = [
  {
    title: "FaceID Pro",
    description: "A modern, high-performance facial recognition system built with Python and OpenCV. Features real-time detection, LBPH identification, and a professional multi-threaded dashboard interface.",
    tech: ["Python", "OpenCV", "CustomTkinter", "LBPH", "Multi-threading", "NumPy"],
    image: FaceIDProImage,
    link: "https://github.com/mileschildress88/FaceID-Pro",
    private: true
  },
  {
    title: "Southwest Airlines Golden Eagles Website",
    description: "The SAGE (Southwest Airlines Golden Eagles) project is a dynamic web application migrated from legacy static HTML to a modern full-stack platform. It serves as a portal for retired pilots, featuring a CMS for managing news, events, officers, resources (health/travel), and a searchable directory of over 1,000 active members.",
    tech: ["Next.js 16", "React 19", "TypeScript", "Tailwind CSS 4", "Supabase", "Prisma", "bcryptjs", "jose (JWT)", "Resend", "Vercel"],
    image: SouthwestAirlinesImage,
    link: "https://github.com/mileschildress88/SAGE",
    private: true
  },
  {
    title: "Global Intelligence Command Center (GICC)",
    description: "A real-time geopolitical and financial intelligence dashboard built around an interactive 3D WebGL globe. Aggregates live data from 30+ sources — tracking conflict events, earthquakes, market movements, and breaking news. An on-board AI analyst named Bob synthesizes active signals and headlines into concise intelligence briefings on demand.",
    tech: ["React 18", "TypeScript", "Vite", "Tailwind CSS", "Three.js", "WebGL", "GLSL Shaders", "Zustand", "Node.js", "Express", "Groq API", "Llama 3.1 8B", "CoinGecko", "Alpha Vantage", "Yahoo Finance", "Frankfurter", "CNN Fear & Greed Index", "USGS Earthquake Feed", "GDELT News API", "RSS (24 global feeds)"],
    image: GICCImage,
    link: "",
    private: true
  },
  {
    title: "Miles Party Pack",
    description: "MilesPartyPack is a Jackbox-inspired local multiplayer game platform featuring six interactive games: Trivia, Hot Takes, Punchline Panic, Bluff Battle, Most Likely To, and Draw & Guess. It is built with a real-time architecture to support mobile-responsive, synchronized lobby play.",
    tech: ["React (Vite)", "TypeScript", "Tailwind CSS", "Framer Motion", "Node.js", "Socket.io", "OpenRouter API", "Zod"],
    image: MilesPartyPackImage,
    link: "https://github.com/mileschildress88/MilesPartyPack",
    private: true
  },
  {
    title: "Personal Portfolio",
    description: "A modern portfolio website built with React, Firebase, and TailwindCSS. Features include dark mode, responsive design, and a professional layout.",
    tech: ["React", "Firebase", "TailwindCSS", "EmailJS", "Google reCAPTCHA"],
    image: LogoImage,
    link: "https://github.com/mileschildress88/Projects",
    private: true
  },
  {
    title: "GymGuardian",
    description: "A gym-themed tower defense game. Defend your gym against waves of enemies using strategic placement of workout equipment and trainers.",
    tech: ["Game Development", "Unity", "C#"],
    image: GymGuardianImage,
    link: "https://github.com/mileschildress88/GymGuardian",
    private: false
  },
  {
    title: "Peptide Calculator",
    description: "A tool that helps users accurately measure and reconstitute peptides for research purposes. Calculates exact volume based on peptide amount, BAC water volume, and desired dosage.",
    tech: ["JavaScript", "HTML", "CSS", "Scientific Computing"],
    image: PeptideCalculatorImage,
    link: "https://github.com/mileschildress88/Peptide-Calculator",
    private: false
  },
  {
    title: "C# Projects",
    description: "A collection of C# projects showcasing various programming concepts and applications developed using the C# programming language.",
    tech: ["C#", ".NET", "Object-Oriented Programming"],
    image: CSharpProjectsImage,
    link: "https://github.com/mileschildress88/CshrpProjects",
    private: false
  }
];

function Portfolio() {
  return (
    <div className="container mx-auto px-4 md:px-8 max-w-7xl" style={{ paddingTop: window.innerWidth <= 768 ? '100px' : '130px', marginBottom: '150px' }}>
      <section className="flex flex-col items-center">
        <motion.div
          className="text-center mb-12 max-w-3xl"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-white" style={{ fontFamily: 'Clash Display, sans-serif', letterSpacing: '-1px' }}>My Projects</h1>
          <p className="text-gray-400 text-lg md:text-xl mb-6">
            A collection of my recent work and personal projects
          </p>
          <div className="inline-block">
            <a 
              href="https://github.com/mileschildress88" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-6 py-3 bg-white/10 hover:bg-white/20 text-white rounded-full transition-all border border-white/10 hover:border-white/20 backdrop-blur-sm"
            >
              <GitHubIcon />
              View All Projects
            </a>
          </div>
        </motion.div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 w-full">
          {projects.map((project, index) => (
            <motion.div
              key={index}
              className="bg-[#111620] border border-[#1e2a3a] rounded-xl overflow-hidden hover:-translate-y-1.5 hover:shadow-2xl transition-all duration-300 flex flex-col h-full"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <div className="relative h-48 md:h-56 overflow-hidden bg-gray-900 group">
                <img 
                  src={project.image} 
                  alt={project.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </div>
              
              <div className="p-6 flex flex-col flex-grow">
                <h3 className="text-xl font-bold mb-3 text-white" style={{ fontFamily: 'Clash Display, sans-serif' }}>{project.title}</h3>
                <p className="text-gray-400 mb-4 text-sm leading-relaxed flex-grow">{project.description}</p>
                
                <div className="flex flex-wrap gap-2 mb-6">
                  {project.tech.map((tech, i) => (
                    <span key={i} className="px-3 py-1 bg-blue-500/10 text-blue-400 text-xs font-medium rounded-full border border-blue-500/20">
                      {tech}
                    </span>
                  ))}
                </div>
                
                <div className="mt-auto pt-4 flex justify-center w-full">
                  {project.private ? (
                    <div className="flex items-center justify-center gap-2 text-gray-600 text-xs bg-white/3 px-5 py-2.5 rounded-lg border border-white/6">
                      <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                        <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                      </svg>
                      <span className="truncate">Private Repository</span>
                    </div>
                  ) : (
                    <a
                      href={project.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 px-6 py-2.5 bg-white/8 hover:bg-white/12 text-white rounded-lg transition-colors font-medium text-xs border border-white/10 hover:border-white/20"
                    >
                      <GitHubIcon className="flex-shrink-0" />
                      <span className="truncate">View Repository</span>
                      <ExternalLinkIcon className="flex-shrink-0" />
                    </a>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
        
        <motion.div 
          className="mt-16 text-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <p className="text-gray-400 mb-6 text-lg">Want to see more of my work or collaborate on a project?</p>
          <a
            href="https://github.com/mileschildress88"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-8 py-3 bg-transparent hover:bg-white/5 border-2 border-white/20 hover:border-white/40 text-white rounded-full transition-all font-medium"
          >
            <GitHubIcon />
            Follow Me on GitHub
          </a>
        </motion.div>
      </section>
    </div>
  );
}

export default Portfolio;
